import { computed } from 'vue';

import { readableTextColor } from '../utils/colorContrast.js';

export function useDesignStyles(design) {
  const getBorderRadiusValue = (size) => {
    const radiusMap = {
      none: '0px',
      small: '4px',
      medium: '12px',
      large: '24px',
      xlarge: '40px',
      full: '9999px',
    };
    return radiusMap[size] || '8px';
  };

  // Inject custom CSS and (for uploaded custom fonts) the @font-face declaration.
  // Google Fonts (string font names like 'Inter') are served locally via the
  // @nuxtjs/google-fonts module — no runtime fetch needed for those.
  const applyCustomCSS = (css) => {
    let fontFaceCSS = '';

    // Only uploaded/custom fonts (object with url) need a runtime @font-face block.
    // Google Font names are already available via the module's SSR-injected stylesheet.
    if (typeof design?.font === 'object' && design.font.url) {
      fontFaceCSS = `@font-face {
          font-family: '${design.font?.original_name?.split('.')[0]}';
          src: url('${design.font?.url}') format('woff2'), url('${design.font?.url}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }\n\n`;
    }

    if (css || fontFaceCSS) {
      const wrappedCSS = `${fontFaceCSS}.fnnl {\n  ${css || ''}\n}`;

      const styleElement = document.createElement('style');
      styleElement.id = 'custom-widget-styles';
      styleElement.textContent = wrappedCSS;

      // Atomically replace the old tag so there is no flash between removal and insertion.
      const existingStyle = document.getElementById('custom-widget-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
      document.head.appendChild(styleElement);
    } else {
      const existingStyle = document.getElementById('custom-widget-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
  };

  if (design?.custom_css || (typeof design?.font === 'object' && design?.font?.url)) {
    applyCustomCSS(design?.custom_css);
  }

  const mainColor = design?.colors?.brand?.primary || '#4f46e5';
  const backgroundColor = design?.colors?.background.primary || '#f8fafc';
  const textColor = design?.colors?.text.default || '#1e293b';
  const answersColor = design?.colors?.text.element || '#1e293b';
  const accentColor = design?.colors?.brand?.secondary || '#4338ca';

  // Mix a tiny bit of the main color with white for answers background
  const answersBackground = mainColor ? adjustColorBrightness(backgroundColor, 30) : '#ffffff';

  const hoverMainColor = adjustColorBrightness(mainColor, -10);

  // Brand colours are customer-picked, so pick button text per WCAG contrast
  const buttonTextColor = readableTextColor(mainColor);
  const buttonHoverTextColor = readableTextColor(hoverMainColor);

  const designStyles = {
    // Brand color
    '--cs-brand-primary': mainColor,
    '--cs-brand-secondary': accentColor,
    // Light tint of the brand color, used for selected cards
    '--cs-brand-primary-light': convertToRgba(mainColor, 0.1),

    // Background colors
    '--cs-background-primary': backgroundColor,

    // Button colors
    '--cs-button-default-background': mainColor,
    '--cs-button-default-text': buttonTextColor,
    '--cs-button-hover-background': hoverMainColor,
    '--cs-button-hover-text': buttonHoverTextColor,

    // Text colors
    '--cs-text-heading': textColor,
    '--cs-text-body': textColor,

    '--cs-link-color': mainColor,

    // Inputs
    '--cs-input-default-background': answersBackground,
    '--cs-input-default-text': answersColor,
    '--cs-input-default-border': convertToRgba(answersColor, 0.2),

    // Choice/Answer colors
    '--cs-choice-default-background': answersBackground,
    '--cs-choice-default-text': answersColor,
    '--cs-choice-default-border': convertToRgba(answersColor, 0.2),

    '--cs-choice-hover-background': mainColor,
    '--cs-choice-hover-text': readableTextColor(mainColor, { dark: answersColor }),
    '--cs-choice-hover-border': convertToRgba(answersColor, 0.4),

    '--cs-choice-active-background': convertToRgba(accentColor, 0.1),
    '--cs-choice-active-text': answersColor,
    '--cs-choice-active-border': accentColor,
    '--cs-choice-active-outline': convertToRgba(accentColor, 0.25),

    // Progress bar
    '--cs-progress-bar-background': convertToRgba(mainColor, 0.2),
    '--cs-progress-bar-fill': mainColor,

    // Icons
    '--cs-success': '#33c25d',
    '--cs-success-500': '#33c25d',
    '--cs-success-700': '#16a34a',

    // Font and border radius
    '--cs-border-radius': getBorderRadiusValue(design?.border_radius || 'medium'),
    '--cs-checkbox-border-radius': design?.border_radius === 'none' ? '0px' : '0.5rem',
    '--cs-font-family': design?.font
      ? typeof design.font === 'string'
        ? `'${design.font}', sans-serif`
        : `'${design.font?.original_name?.split('.')[0]}', sans-serif`
      : 'Inter, sans-serif',
  };

  return {
    designStyles,
  };
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex, percent) {
  // Convert hex to RGB
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  // Adjust brightness
  r = Math.max(0, Math.min(255, r + (r * percent) / 100));
  g = Math.max(0, Math.min(255, g + (g * percent) / 100));
  b = Math.max(0, Math.min(255, b + (b * percent) / 100));

  // Convert back to hex
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

function convertToRgba(hex, opacity) {
  // Convert hex to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Return rgba string
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
